import React, { useState } from 'react';
import { toast } from 'sonner';
import { NotebookPen, Trash2, Phone, Users, Mail, MessageCircle, MoreHorizontal } from 'lucide-react';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Field, inputCls } from '@/components/admin/commerce/shared';

const KINDS = ['call', 'meeting', 'email', 'whatsapp', 'other'];
const ICON = { call: Phone, meeting: Users, email: Mail, whatsapp: MessageCircle, other: MoreHorizontal };
const fmt = (iso) => new Date(iso).toLocaleString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

export const ProspectNotesDialog = ({ prospect, onClose, onChanged, t }) => {
  const [kind, setKind] = useState('call');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const notes = [...(prospect?.contact_notes || [])].reverse();
  const add = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/admin/prospects/${prospect.id}/notes`, { kind, text }); setText(''); toast.success(t('prospects.notes.added')); onChanged(data); }
    catch (e) { toast.error(e?.response?.data?.detail || t('adminCommerce.saveError')); }
    finally { setBusy(false); }
  };
  const remove = async (n) => { try { const { data } = await api.delete(`/admin/prospects/${prospect.id}/notes/${n.id}`); onChanged(data); } catch (_) { toast.error(t('adminCommerce.saveError')); } };
  if (!prospect) return null;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="prospect-notes-dialog">
        <DialogHeader><DialogTitle className="inline-flex items-center gap-2"><NotebookPen className="h-5 w-5 text-emerald-600" />{t('prospects.notes.title', { company: prospect.company })}</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">{t('prospects.notes.hint')}</p>
        <div className="grid grid-cols-[140px_1fr] gap-3 items-start">
          <Field label={t('prospects.notes.kind')}>
            <select className={inputCls} value={kind} onChange={(e) => setKind(e.target.value)} data-testid="prospect-note-kind">{KINDS.map((k) => <option key={k} value={k}>{t(`prospects.notes.${k}`)}</option>)}</select>
          </Field>
          <Field label={t('prospects.notes.text')}>
            <Textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder={t('prospects.notes.placeholder')} className="mt-1" data-testid="prospect-note-text" />
          </Field>
        </div>
        <div className="flex justify-end"><Button onClick={add} disabled={busy || !text.trim()} className="bg-emerald-600 hover:bg-emerald-700" data-testid="prospect-note-add-btn">{busy ? '…' : t('prospects.notes.add')}</Button></div>
        <ul className="max-h-64 overflow-y-auto divide-y divide-slate-100 border rounded-lg" data-testid="prospect-notes-list">
          {notes.length === 0 && <li className="p-3 text-sm text-slate-500 text-center">{t('prospects.notes.empty')}</li>}
          {notes.map((n) => { const Icon = ICON[n.kind] || MoreHorizontal; return (
            <li key={n.id} className="p-3 flex gap-3 items-start text-sm" data-testid={`prospect-note-${n.id}`}>
              <Icon className="h-4 w-4 text-slate-500 mt-0.5 flex-none" />
              <div className="flex-1 min-w-0"><p className="text-slate-800 whitespace-pre-line">{n.text}</p><p className="text-[11px] text-slate-500 mt-0.5">{t(`prospects.notes.${n.kind}`)} · {fmt(n.at)}{n.by ? ` · ${n.by}` : ''}</p></div>
              <button type="button" onClick={() => remove(n)} className="text-slate-400 hover:text-rose-600" title={t('prospects.notes.delete')} data-testid={`prospect-note-delete-${n.id}`}><Trash2 className="h-4 w-4" /></button>
            </li>); })}
        </ul>
      </DialogContent>
    </Dialog>
  );
};
